/* eslint-disable max-classes-per-file */
import React, { Component } from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import MuiAlert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import TableCell from '@mui/material/TableCell';
import withStyles from '@mui/styles/withStyles';
import Tooltip from '@mui/material/Tooltip';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Cookies from 'universal-cookie';

import Topbar from './Topbar';

window.saferApiHost = undefined;

// Must match safer.utils.ORG_API_DEBUG_PORTS (LEGACY_VALID_ORGS order).
const LOCAL_API_DEBUG_PORTS = {
  master: 8100,
  famb: 8101,
  ostgota: 8102,
  fmv: 8103,
  tidomat: 8104,
  detertech: 8105,
  boliden: 8106,
  underwater: 8107,
};

function localhostApiHost(org) {
  // predict.py with --debug=0 listens on 8080; per-org debug ports need
  // REACT_APP_API_PORT or an org slug (8101, 8102, …) when using --debug=1.
  const port = process.env.REACT_APP_API_PORT
    || (org && org !== MASTER_ORG && LOCAL_API_DEBUG_PORTS[org])
    || '8080';
  return `http://localhost:${port}`;
}

// Navigation utility for client-side routing
let navigationHistory = null;

export function setNavigationHistory(history) {
  navigationHistory = history;
}

export function navigateTo(path) {
  if (navigationHistory) {
    navigationHistory.push(path);
  } else {
    // Fallback to window.location if history is not available
    window.location.replace(path);
  }
}

export const MASTER_ORG = 'master';

export function isMasterOrg(profile) {
  return profile?.org === MASTER_ORG;
}

export function canAccessOrgAdmin(profile) {
  if (!profile) {
    return false;
  }
  if (isMasterOrg(profile)) {
    return true;
  }
  return profile.role === 'admin';
}

export function fetchUserMeProfile(apiHost, authResponse, { authScheme, tokenId } = {}) {
  const scheme = authScheme || getAuthScheme();
  if (scheme !== 'Firebase') {
    return Promise.resolve(authResponse);
  }

  const token = tokenId || window.saferGoogleToken;
  if (!token) {
    return Promise.resolve(authResponse);
  }

  return fetch(`${apiHost}/v1/user/me`, {
    headers: {
      Authorization: `${scheme} ${token}`,
    },
  })
    .then((res) => (res.ok ? res.json() : null))
    .then((me) => {
      if (!me) {
        return authResponse;
      }
      return {
        ...authResponse,
        role: me.role ?? authResponse.role,
      };
    })
    .catch(() => authResponse);
}

function isLocalDevHost() {
  const { hostname } = window.location;
  return hostname === 'localhost' || hostname === '127.0.0.1';
}

export function getApiHost(callback) {
  if (isLocalDevHost()) {
    callback(localhostApiHost(window.saferUserOrg));
    return;
  }
  if (window.saferApiHost === undefined) {
    const { protocol } = window.location;
    if (isProd())
      window.saferApiHost = `${protocol}//api.ser.safereaction.com`;
    else // for microk8s environment
      // TODO: this should not work, process.env is replaced during build
      window.saferApiHost = (
        `${protocol}//${process.env.REACT_APP_API_SERVICE_HOST}`);
  }
  callback(window.saferApiHost);
}

export function isProd() {
  const overlay = process.env.REACT_APP_K8S_OVERLAY;
  return overlay === 'gke' || overlay === 'mke';
}

export function getAuthScheme() {
  return window.saferAuthScheme || 'Firebase';
}

export function isValidAuthToken(token) {
  if (!token) {
    return false;
  }
  if (token === 'fakeToken') {
    return !isProd();
  }
  const parts = token.split('.');
  return parts.length === 3 && parts.every(Boolean) && token.length > 100;
}

export function loadStoredAuthToken() {
  const saferCookies = new Cookies();
  const token = saferCookies.get('saferGoogleToken');
  if (!isValidAuthToken(token)) {
    if (token) {
      const cookieOptions = authCookiePathOptions();
      saferCookies.remove('saferGoogleToken', cookieOptions);
      saferCookies.remove('saferAuthScheme', cookieOptions);
    }
    return undefined;
  }
  return token;
}

export function getAuthHeaders(extraHeaders = {}) {
  if (!window.saferGoogleToken) {
    return extraHeaders;
  }
  return {
    Authorization: `${getAuthScheme()} ${window.saferGoogleToken}`,
    ...extraHeaders,
  };
}

export function getAuthValidatePath() {
  return '/v1/firebase_auth';
}

function authCookiePathOptions() {
  return {path: '/' };
}

export function clearAuthSession() {
  window.saferGoogleToken = undefined;
  window.saferAuthScheme = undefined;
  const saferCookies = new Cookies();
  const cookieOptions = authCookiePathOptions();
  saferCookies.remove('saferGoogleToken', cookieOptions);
  saferCookies.remove('saferAuthScheme', cookieOptions);
}

export function fetchApiRequest(url, callback, init={}) {
  if (window.saferGoogleToken) {
    const { headers, ...initNoHeaders } = init;
    const requestInit = {
      headers: getAuthHeaders(headers),
      ...initNoHeaders
    };
    const fetchPromise = fetch(url, requestInit);
    fetchPromise.then((res) => {
      if (res.status === 401) {
        window.saferLogoutInProgress = true;
        clearAuthSession();
        // Only navigate if not already on login page
        if (!window.location.hash.includes('/login')) {
          navigateTo('/#/login');
        }
        return;
      }
      callback(fetchPromise);
    }).catch((err) => {
      callback(Promise.reject(err));
    });
  } else {
    // Only navigate if not already on login page
    if (!window.location.hash.includes('/login')) {
      navigateTo('/#/login');
    }
  }
}

export function buildRequestsUrl(state, params, context, callback) {
  getApiHost((apiHost) => {
    const apiMethod = state.similar
      ? 'similar_requests'
      : 'annotation_requests';
    const requestsUrl = `${apiHost}/v1/${apiMethod}?${params.toString()}`;
    callback(requestsUrl);
  });
}

export function getLocationParam(param) {
  return new URLSearchParams(window.location.search).get(param);
}

export function updateLocationSearchParams(params) {
  // Extract current search parameters from the main URL
  const search = new URLSearchParams(window.location.search);
  Object.entries(params).forEach(([key, value]) => search.set(key, value));

  // Construct new URL with updated search params and current hash
  const newPath = `/?${search.toString()}${window.location.hash}`;
  window.history.pushState({}, '', newPath);

  // Trigger a custom event to notify components that URL params have changed
  window.dispatchEvent(new CustomEvent('urlParamsChanged', {
    detail: { params: Object.fromEntries(search) }
  }));
}

export function loadConfig(params) {
  const search = new URLSearchParams(window.location.search);
  Object.entries(params).map((e) => search.set(e[0], e[1]));
  window.location.search = search.toString();
}

export const TableCellHead = withStyles({
  root: {
    fontWeight: 'bold',
    verticalAlign: 'top',
  },
})(TableCell);

export function ifdef(variable, fun = (v) => v) {
  if (typeof variable !== 'undefined')
    return fun(variable);
  else
    return <i>&lt;undefined&gt;</i>;
}

export function toString(value) {
  return value === null ? '' : value;
}

export class SnackAlert extends React.Component {
  constructor(props) {
    super(props);
    this.hideDuration =
      props.hideDuration === undefined ? 6000 : props.hideDuration;
    this.handleClose = this.handleClose.bind(this);
  }

  handleClose(event, reason) {
    const { parent, state } = this.props;
    if (reason !== 'clickaway') {
      parent.setState({ [state]: false });
    }
  }

  render() {
    const { parent, state, severity, children } = this.props;
    return (
      <Snackbar
        open={parent.state[state]}
        autoHideDuration={this.hideDuration}
        onClose={this.handleClose}
        disableWindowBlurListener
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          onClose={this.handleClose}
          severity={severity}
        >
          {children}
        </MuiAlert>
      </Snackbar>
    );
  }
}

export function renderBoilerPlate(children) {
  return (<>
    <CssBaseline />
    <Topbar />
    { children }
  </>);
}

export function loadingBackdrop({ open }) {
  return (
    <Backdrop open={open} style={{ zIndex: 1500, color: '#fff' }}>
      <CircularProgress color="inherit" />
    </Backdrop>
  );
}

export class TooltipTableCell extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isOverflowed: false,
    };
    this.textElement = React.createRef();
  }

  componentDidMount() {
    const { scrollWidth, clientWidth } = this.textElement.current;
    this.setState({ isOverflowed: scrollWidth > clientWidth });
  }

  render() {
    const { isOverflowed } = this.state;
    const { children } = this.props;
    return (
      <Tooltip
        interactive="true"
        title={children || ''}
        disableHoverListener={!isOverflowed}
      >
        <TableCell ref={this.textElement}>{children}</TableCell>
      </Tooltip>
    );
  }
}

export function loadEvents(callback, audio_url) {
  getApiHost(apiHost => {
    let url = `${apiHost}/v1/events`;
    if (audio_url !== undefined)
      url += `?audio_url=${audio_url}`
    fetchApiRequest(url, fetch => fetch
      .then(res => {
        if (res.ok)
          res.json().then(data => {
            data.events.push({id: 999, name: 'Ignore'})
            callback(data.events)
          });
        else
          res.text().then(error => alert(
            `Fetch events response error: ${error}`));
      })
      .catch(error => alert(`Fetch events error: ${error}`))
    );
  });
}

export function loadOrgs(context, callback) {
  getApiHost(apiHost => {
    let url = `${apiHost}/v1/orgs`;
    fetchApiRequest(url, fetch => fetch
      .then(res => {
        if (res.ok)
          res.json().then(data => callback(data.orgs));
        else
          res.text().then(error => alert(
            `Fetch orgs response error: ${error}`));
      })
      .catch(error => alert(`Fetch orgs error: ${error}`))
    );
  });
}

export function zeroFill(value) {
  const number = parseInt(value);
  if (number < 10) return `0${number}`;
  return number.toString();
}
